import type { ComposeChoice, WidgetDraft } from '@/features/mitra/api/dashboard'

/**
 * Query keys for the reader's dashboards on the Mitra engine.
 *
 * A board is keyed by its id, `first` for the reader's first dashboard (what
 * `/v1/dashboard` serves), so a board read by id and the same board read as
 * the first never share an entry that could disagree about its etag.
 *
 * A preview is keyed by the definitions it was asked about, so two drafts
 * never share an answer and a reply for a draft the reader has since edited
 * lands under its own key rather than on the current one. That is what keeps
 * the formula box's live check from showing an earlier keystroke's verdict.
 */
export const dashboardKeys = {
  all: ['mitra-dashboard'] as const,
  boards: () => [...dashboardKeys.all, 'boards'] as const,
  board: (id: string | null = null) => [...dashboardKeys.all, 'board', id ?? 'first'] as const,
  vocabulary: () => [...dashboardKeys.all, 'vocabulary'] as const,
  preview: (widgets: readonly WidgetDraft[]) =>
    [
      ...dashboardKeys.all,
      'preview',
      widgets.map((w) => [
        w.id ?? '',
        w.title,
        w.formula,
        w.kind,
        w.unit,
        w.decimals,
        JSON.stringify(w.scope ?? null),
        // A window, an order or a cut changes the answer as much as the formula.
        JSON.stringify(w.time ?? null),
        w.rank ?? '',
        w.limit ?? '',
        w.where ?? '',
      ]),
    ] as const,
  compose: (choice: ComposeChoice) =>
    [...dashboardKeys.all, 'compose', JSON.stringify(choice)] as const,
}
